import {DateTime} from "luxon"

/**
 * Normalise a stored Room Condition send time to "HH:mm" (what the shared `time`
 * field reads/writes). The backend has stored these different ways over time, so
 * an existing template can carry any of:
 *
 *   - a full datetime string  ("2026-07-23 07:00:00")  -- the historic default
 *   - an "HH:mm" string        ("07:00")                -- what this port writes
 *   - an hour integer          (7)                       -- an older shape
 *
 * All resolve to "HH:mm" so an existing template edits correctly. Returns null
 * for anything unparseable, so the caller can drop it rather than render an
 * empty picker. The backend only reads the hour, so a normalised value round-
 * trips unchanged.
 *
 * @param {string|number} value
 * @param {string} timezone
 * @returns {string|null}
 */
export function normalizeSendTime(value, timezone = 'America/New_York') {
    if (value === null || value === undefined || value === '') {
        return null
    }

    // Hour integer (or a numeric string that is just an hour).
    if (typeof value === 'number' || /^\d{1,2}$/.test(String(value).trim())) {
        const hour = Number(value)
        if (! Number.isInteger(hour) || hour < 0 || hour > 23) {
            return null
        }
        return `${String(hour).padStart(2, '0')}:00`
    }

    const raw = String(value).trim()

    // Already "HH:mm" (or "H:mm").
    const hhmm = DateTime.fromFormat(raw, 'HH:mm', {zone: timezone})
    if (hhmm.isValid) {
        return hhmm.toFormat('HH:mm')
    }

    // A full datetime string, in one of the shapes the backend has stored.
    const asIso = DateTime.fromISO(raw, {zone: timezone})
    if (asIso.isValid) {
        return asIso.toFormat('HH:mm')
    }
    const asSql = DateTime.fromSQL(raw, {zone: timezone})
    if (asSql.isValid) {
        return asSql.toFormat('HH:mm')
    }

    // Last resort: let the JS Date parser try (covers a few odd formats).
    const parsed = new Date(raw)
    if (! isNaN(parsed.getTime())) {
        return DateTime.fromJSDate(parsed, {zone: timezone}).toFormat('HH:mm')
    }

    return null
}
